import styled from 'styled-components'
import { CustomStyleOverrideProps } from '../FormInput/types'
import {
  getBrandPrimaryColor,
  getDangerColor,
  getTextPrimaryColor,
  getTextSecondaryColor,
} from '../FormInput/utils'

type Variant =
  | 'body'
  | 'body-sm'
  | 'body-xs'
  | 'dot-lg'
  | 'label-sm'
  | 'label'
  | 'number-lg'
  | 'number-lg-secondary'
  | 'number-lg-discount'
  | 'number-lg-sale'
  | 'number'
  | 'number-sm'
  | 'number-secondary'
  | 'number-sm-secondary'
  | 'number-sm-discount'
  | 'number-sm-sale'
  | 'number-discount'
  | 'link'
  | 'link-sm'
  | 'link-secondary'
  | 'link-sm-secondary'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'

export const getVariantStyles = (
  variant?: Variant,
  baseOverrides?: CustomStyleOverrideProps
) => {
  const textPrimaryColor = getTextPrimaryColor(baseOverrides)
  const textSecondaryColor = getTextSecondaryColor(baseOverrides)
  switch (variant) {
    case 'body-sm':
      return `
        color: ${textSecondaryColor};
        font-size: 0.813rem;
      `
    case 'body-xs':
      return `
        color: ${textSecondaryColor};
        font-size: 0.625rem;
      `
    case 'dot-lg':
      return `
        font-size: 1.5rem;
        color: ${textPrimaryColor};
      `
    case 'label-sm':
      return `
        color: ${textSecondaryColor};
        font-size: 0.75rem;
      `
    case 'label':
      return `
        color: ${textSecondaryColor};
        font-size: 0.813rem;
        font-weight: bold;
      `
    case 'number-sm':
      return `
        color: ${textPrimaryColor};
        font-size: 0.8125rem;
      `
    case 'number-lg':
      return `
        font-size: 1.5rem;
        color: ${textPrimaryColor};
      `
    case 'number-lg-secondary':
      return `
        font-size: 1.5rem;
        color: ${textSecondaryColor};
      `
    case 'number':
      return `
        font-size: 1rem;
        color: ${textPrimaryColor};
      `
    case 'number-secondary':
      return `
        font-size: 1rem;
        color: ${textSecondaryColor};
      `
    case 'number-discount':
      return `
        font-size: 1rem;
        text-decoration: line-through;
        color: ${textSecondaryColor};
      `
    case 'number-sm-secondary':
      return `
        font-size: 0.8125rem;
        color: ${textSecondaryColor};
      `
    case 'number-lg-sale':
      return `
        font-size: 1.5rem;
        color: ${getDangerColor(baseOverrides)};
      `
    case 'link':
      return `
        font-size: 1rem;
        font-weight: bold;
        color: ${getBrandPrimaryColor(baseOverrides)};
      `
    case 'link-sm':
      return `
        font-size: 0.8125rem;
        color: ${getBrandPrimaryColor(baseOverrides)};
        font-weight: bold;
      `
    case 'link-secondary':
      return `
        font-size: 1rem;
        font-weight: bold;
        color: ${textSecondaryColor};
      `
    case 'link-sm-secondary':
      return `
        font-size: 0.8125rem;
        color: ${textSecondaryColor};
        font-weight: bold;
      `
    case 'h1':
      return `
        font: var(--font-primary);
        margin-top: 0;
        font-size: 2.25rem;
        font-weight: bold;
      `
    case 'h2':
      return `
        font: var(--font-primary);
        margin-top: 0;
        font-size: 1.5rem;
        font-weight: bold;
      `
    case 'h3':
      return `
        font: var(--font-primary);
        margin-top: 0;
        font-size: 1rem;
        font-weight: bold;
      `
    case 'h4':
      return `
        font: var(--font-primary);
        margin-top: 0;
        font-size: 0.75rem;
        font-weight: bold;
        letter-spacing: 1px;
        color: ${textSecondaryColor};
        text-transform: uppercase;
      `
    // case 'body':
    default:
      return `
        color: ${textPrimaryColor};
        font: var(--font-primary);
        font-size: 1rem;
        font-weight: 400;
      `
  }
}

export const getFont = (variant?: Variant) =>
  variant?.startsWith('number')
    ? 'var(--font-family-secondary), sans-serif'
    : 'var(--font-family-primary), sans-serif'

export type TypographyProps = React.HTMLAttributes<HTMLDivElement> & {
  variant?: Variant
  role?: string
  'aria-level'?: number
  baseOverrides?: CustomStyleOverrideProps
}

export const getTypographyStyles = (
  variant?: Variant,
  baseOverrides?: CustomStyleOverrideProps
) =>
  `
  font-family: ${getFont(variant)};
  font-display: swap;
  font-weight: 400;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.5;
  letter-spacing: 0.4px;
  color: var(--text-primary);
  ${getVariantStyles(variant, baseOverrides)}
`

const Typography = styled.div<TypographyProps>`
  ${(props) => getTypographyStyles(props.variant, props.baseOverrides)}
  role: ${(props) => (props.variant === 'h1' || props.variant === 'h2' || props.variant === 'h3' || props.variant === 'h4' ? 'heading' : undefined)};
  aria-level: ${(props) => (props.variant === 'h1' ? 1 : props.variant === 'h2' ? 2 : props.variant === 'h3' ? 3 : props.variant === 'h4' ? 4 : undefined)};
`

export default Typography